// src/components/layout/modals/DocumentModal.tsx
// ---------------------------------------------------------------------------
// Document viewer modal with navigation and image zoom/pan support.
// ---------------------------------------------------------------------------
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "../../../i18n";
import {
  downloadDocumentFromUrl,
  DownloadForbiddenError,
} from "../../../utils/downloadDocument";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DocumentModalProps {
  docs: string[];
  index: number | null;
  onClose: () => void;
  onNavigate: (newIdx: number) => void;
  /**
   * Optional per-doc gated download URLs (parallel to `docs`). When present for the current
   * doc, the "Download" action fetches the file through the EXPORT-scope-gated endpoint
   * (066 B / option A) and refuses with a message if out of scope; when absent it falls back
   * to opening the view URL. Viewing/preview always uses `docs` (the view scope).
   */
  downloadUrls?: (string | undefined)[];
  /** Optional per-doc filenames (parallel to `docs`) for the saved download. */
  fileNames?: (string | undefined)[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getFileExtension = (url: string): string =>
  url.split("?")[0].split(".").pop()?.toLowerCase() || "";

const isImageExt = (ext: string) => /(jpg|jpeg|png|gif|bmp|webp|tiff|tif|svs)$/i.test(ext);

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 20;
const ZOOM_STEP = 0.25;

// ---------------------------------------------------------------------------
// Text content sub-component
// ---------------------------------------------------------------------------

const TextFileContent: React.FC<{ url: string }> = ({ url }) => {
  const { t } = useTranslation();
  const [textContent, setTextContent] = useState<string>(t("document_modal.loading", "Loading..."));

  useEffect(() => {
    fetch(url)
      .then((res) => res.text())
      .then((text) => setTextContent(text))
      .catch(() => setTextContent(t("document_modal.failed_to_load", "Failed to load text file")));
  }, [url]);

  return (
    <Box
      sx={{
        p: 3,
        maxHeight: "70vh",
        overflow: "auto",
        backgroundColor: "background.default",
        borderRadius: 1,
      }}
    >
      <Typography
        variant="body1"
        component="pre"
        sx={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontFamily: "monospace",
        }}
      >
        {textContent}
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Image viewer with zoom / pan
// ---------------------------------------------------------------------------

interface ImageViewerProps {
  src: string;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ src }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

  // Pinch-to-zoom state
  const lastPinchDist = useRef<number | null>(null);

  // Reset on image change
  useEffect(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, [src]);

  // Zoom with mouse wheel
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((z) => {
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta * Math.max(1, z * 0.3)));
    });
  }, []);

  // Pan with mouse drag
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return; // left button only
      e.preventDefault();
      setDragging(true);
      dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    },
    [offset]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging) return;
      setOffset({
        x: dragStart.current.ox + (e.clientX - dragStart.current.x),
        y: dragStart.current.oy + (e.clientY - dragStart.current.y),
      });
    },
    [dragging]
  );

  const handleMouseUp = useCallback(() => setDragging(false), []);

  // Touch support: pinch-to-zoom and single-finger pan
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (lastPinchDist.current !== null) {
          const scale = dist / lastPinchDist.current;
          setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z * scale)));
        }
        lastPinchDist.current = dist;
      } else if (e.touches.length === 1 && dragging) {
        setOffset({
          x: dragStart.current.ox + (e.touches[0].clientX - dragStart.current.x),
          y: dragStart.current.oy + (e.touches[0].clientY - dragStart.current.y),
        });
      }
    },
    [dragging]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        setDragging(true);
        dragStart.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          ox: offset.x,
          oy: offset.y,
        };
      }
      lastPinchDist.current = null;
    },
    [offset]
  );

  const handleTouchEnd = useCallback(() => {
    setDragging(false);
    lastPinchDist.current = null;
  }, []);

  // Double-click to reset
  const handleDoubleClick = useCallback(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Zoom controls (exposed for the toolbar)
  const zoomIn = useCallback(() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP)), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP)), []);
  const resetView = useCallback(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
      {/* Image canvas */}
      <Box
        ref={containerRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onDoubleClick={handleDoubleClick}
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: dragging ? "grabbing" : "grab",
          userSelect: "none",
          touchAction: "none",
          position: "relative",
          backgroundColor: "rgba(0,0,0,0.03)",
          borderRadius: 1,
        }}
      >
        <img
          src={src}
          alt="document"
          draggable={false}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transformOrigin: "center center",
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            transition: dragging ? "none" : "transform 0.15s ease-out",
          }}
        />
      </Box>

      {/* Zoom toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          pt: 1,
        }}
      >
        <IconButton size="small" onClick={zoomOut} title={t("document_modal.zoom_out", "Zoom out")}>
          <ZoomOutIcon fontSize="small" />
        </IconButton>
        <Typography variant="caption" sx={{ minWidth: 48, textAlign: "center", fontFamily: "monospace" }}>
          {Math.round(zoom * 100)}%
        </Typography>
        <IconButton size="small" onClick={zoomIn} title={t("document_modal.zoom_in", "Zoom in")}>
          <ZoomInIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={resetView} title={t("document_modal.reset_view", "Reset view")}>
          <CenterFocusStrongIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main Modal
// ---------------------------------------------------------------------------

const DocumentModal: React.FC<DocumentModalProps> = ({
  docs,
  index,
  onClose,
  onNavigate,
  downloadUrls,
  fileNames,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Gated download: prefer the export-scoped download endpoint; fall back to opening the
  // view URL when no gated URL is supplied for this doc (066 B / option A).
  const handleDownload = useCallback(
    async (docIndex: number) => {
      const gatedUrl = downloadUrls?.[docIndex];
      const viewUrl = docs[docIndex];
      if (!gatedUrl) {
        window.open(viewUrl, "_blank");
        return;
      }
      try {
        await downloadDocumentFromUrl(gatedUrl, fileNames?.[docIndex]);
      } catch (err) {
        if (err instanceof DownloadForbiddenError) {
          enqueueSnackbar(
            t(
              "document_modal.download_forbidden",
              "You can view this document but do not have permission to download it. Downloading is limited to environments where your role allows export."
            ),
            { variant: "warning" }
          );
        } else {
          enqueueSnackbar(
            t("document_modal.download_failed", "Could not download the document."),
            { variant: "error" }
          );
        }
      }
    },
    [docs, downloadUrls, fileNames, enqueueSnackbar, t]
  );

  // Keyboard navigation
  useEffect(() => {
    if (index === null || docs.length === 0) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") onNavigate((index + 1) % docs.length);
      if (e.key === "ArrowLeft")
        onNavigate((index - 1 + docs.length) % docs.length);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [index, docs.length, onNavigate]);

  if (index === null || docs.length === 0) return null;

  const selectedDoc = docs[index];
  const ext = getFileExtension(selectedDoc);

  let content: JSX.Element;
  if (isImageExt(ext)) {
    // Enhanced image viewer with zoom & pan
    content = <ImageViewer src={selectedDoc} />;
  } else if (ext === "pdf") {
    content = (
      <iframe
        src={selectedDoc}
        title="PDF Viewer"
        width="100%"
        height="100%"
        style={{ border: "none" }}
      />
    );
  } else if (/^docx?$|^pptx?$|^xlsx?$/.test(ext)) {
    const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
      selectedDoc
    )}`;
    content = (
      <iframe
        src={officeViewerUrl}
        title="Office Document Preview"
        width="100%"
        height="100%"
        style={{ border: "none" }}
      />
    );
  } else if (/^(mp3|wav|webm)$/i.test(ext)) {
    content = (
      <Box sx={{ p: 2 }}>
        <audio controls src={selectedDoc} style={{ width: "100%" }} />
      </Box>
    );
  } else if (/^(mp4|mov)$/i.test(ext)) {
    content = <video controls src={selectedDoc} style={{ width: "100%" }} />;
  } else if (ext === "txt") {
    content = <TextFileContent url={selectedDoc} />;
  } else {
    content = (
      <Typography variant="body1" color="text.secondary">
        {t("document_modal.preview_not_available", "Preview not available for .{ext}").replace("{ext}", ext.toUpperCase())}{" "}
        <a
          href={selectedDoc}
          onClick={(e) => {
            e.preventDefault();
            handleDownload(index);
          }}
          rel="noreferrer"
        >
          {t("action.download", "Download")}
        </a>
      </Typography>
    );
  }

  return (
    <Dialog open={index !== null} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {t("document_modal.title", "Document Preview")}
        {docs.length > 1 && (
          <Typography variant="caption" sx={{ ml: 2, opacity: 0.6 }}>
            {index + 1} / {docs.length}
          </Typography>
        )}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ height: "80vh", width: "100%" }}>{content}</Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button
          onClick={() => onNavigate((index - 1 + docs.length) % docs.length)}
          disabled={docs.length <= 1}
        >
          {t("document_modal.prev", "‹ Prev")}
        </Button>
        <Button onClick={() => handleDownload(index)}>
          {t("action.download", "Download")}
        </Button>
        <Button
          onClick={() => onNavigate((index + 1) % docs.length)}
          disabled={docs.length <= 1}
        >
          {t("document_modal.next", "Next ›")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentModal;
